import { useCallback, useEffect, useRef } from "react";

import {
  addSubscription,
  removeSubscription,
} from "../../channels/subscriptionManager";

interface UseWebSocketProps {
  [key: string]: unknown;
  canCreateConnection?: boolean;
  channel: string;
  onConnected?: () => void;
  onMessageReceive: (data: any) => void | Promise<void>;
}

export const useWebSocket = (
  {
    channel,
    onConnected,
    onMessageReceive,
    canCreateConnection = true, // If we don't want to create a connection while certain conditions are not met, ex: community_member is not present?
    ...passThroughParams
  }: UseWebSocketProps,
  deps: unknown[] = [],
) => {
  const onConnectedRef = useRef(onConnected);
  const onMessageReceiveRef = useRef(onMessageReceive);
  const subscriptionsByChannelRef = useRef<any>({});

  const paramsKey = JSON.stringify(passThroughParams);
  const depsKey = JSON.stringify(deps);
  const stableParamsRef = useRef(passThroughParams);
  stableParamsRef.current = passThroughParams;
  onConnectedRef.current = onConnected;
  onMessageReceiveRef.current = onMessageReceive;

  const stableOnConnected = useCallback(() => onConnectedRef.current?.(), []);

  const stableOnMessageReceive = useCallback(
    (data: any) => onMessageReceiveRef.current(data),
    [],
  );

  useEffect(() => {
    if (!canCreateConnection) {
      return;
    }

    const params = stableParamsRef.current;
    const subscriptionObj = addSubscription(
      { channel, ...params },
      stableOnMessageReceive,
    );
    const removeConnectListener = subscriptionObj?.subscription.on(
      "connect",
      stableOnConnected,
    );
    if (subscriptionObj?.subscription.state === "connected") {
      stableOnConnected();
    }
    if (subscriptionObj) {
      subscriptionsByChannelRef.current = {
        ...subscriptionsByChannelRef.current,
        [channel]: subscriptionObj,
      };
    }

    return () => {
      removeConnectListener?.();
      removeSubscription({ channel, ...params }, stableOnMessageReceive);
      subscriptionsByChannelRef.current = {
        ...subscriptionsByChannelRef.current,
        [channel]: null,
      };
    };
  }, [
    channel,
    canCreateConnection,
    paramsKey,
    depsKey,
    stableOnConnected,
    stableOnMessageReceive,
  ]);

  return subscriptionsByChannelRef.current[channel];
};
